import React, { useEffect, useState } from 'react';
import {
  isPushSupported, notificationPermission,
  isPushSubscribed, enablePushNotifications,
  disablePushNotifications, sendTestPushToSelf,
} from '../utils/pushNotifications';

/**
 * Self-contained card the contractor sees on their dashboard. Renders three
 * states:
 *
 *   - Subscribed:    "✓ Reminders on" + Disable button + Test button
 *   - Not subscribed: "🔔 Enable reminders" CTA + 1-line explainer
 *   - Permission blocked OR browser unsupported: explanatory message,
 *                     no controls
 */
export default function PushReminderToggle() {
  const [supported,  setSupported]  = useState(true);
  const [perm,       setPerm]       = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [busy,       setBusy]       = useState(false);
  const [error,      setError]      = useState('');
  const [testStatus, setTestStatus] = useState('');

  useEffect(() => {
    setSupported(isPushSupported());
    setPerm(notificationPermission());
    if (isPushSupported()) {
      isPushSubscribed().then(setSubscribed).catch(() => setSubscribed(false));
    }
  }, []);

  async function handleEnable() {
    setBusy(true); setError(''); setTestStatus('');
    try {
      await enablePushNotifications();
      setSubscribed(true);
      setPerm('granted');
    } catch (err) {
      setError(err.message || 'Erro ao ativar notificações.');
      // Re-check permission so the UI reflects what the OS actually decided
      setPerm(notificationPermission());
    } finally {
      setBusy(false);
    }
  }

  async function handleDisable() {
    setBusy(true); setError(''); setTestStatus('');
    try {
      await disablePushNotifications();
      setSubscribed(false);
    } catch (err) {
      setError(err.message || 'Erro ao desativar.');
    } finally {
      setBusy(false);
    }
  }

  async function handleTest() {
    setTestStatus('Enviando…');
    try {
      const r = await sendTestPushToSelf();
      if (r?.sent > 0) setTestStatus('✓ Enviado! Veja a notificação.');
      else setTestStatus('Nada enviado — talvez o navegador bloqueou.');
    } catch (err) {
      setTestStatus(err.message || 'Falha ao testar.');
    }
  }

  if (!supported) {
    return (
      <div className="card" style={cardStyleSubtle}>
        <div style={{ fontSize: 13, color: '#475569' }}>
          📱 Seu navegador não suporta notificações push.
          {/iPhone|iPad|iPod/i.test(navigator.userAgent) &&
            <> Adicione o app à tela inicial (Compartilhar → Adicionar à Tela de Início) pra habilitar.</>}
        </div>
      </div>
    );
  }

  if (perm === 'denied') {
    return (
      <div className="card" style={cardStyleWarn}>
        <div style={{ fontSize: 13, color: '#92400E', marginBottom: 4 }}>
          🔕 Notificações estão <strong>bloqueadas</strong> neste navegador.
        </div>
        <div style={{ fontSize: 12, color: '#92400E' }}>
          Pra ativar: clica no cadeado/⚙ ao lado da URL → Permissões → Notificações → Permitir.
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={subscribed ? cardStyleOn : cardStyleOff}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 22 }}>{subscribed ? '🔔' : '🔕'}</div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: subscribed ? '#15803D' : '#0F172A' }}>
            {subscribed ? 'Lembretes de relógio ativados' : 'Ativar lembretes de relógio'}
          </div>
          <div style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>
            {subscribed
              ? 'Você vai receber uma notificação de manhã se ainda não tiver batido o ponto.'
              : 'Receba uma notificação 7:55 da manhã se ainda não tiver batido o ponto.'}
          </div>
        </div>
        {!subscribed && (
          <button onClick={handleEnable} disabled={busy} style={btnPrimary}>
            {busy ? '…' : 'Ativar'}
          </button>
        )}
        {subscribed && (
          <>
            <button onClick={handleTest} disabled={busy} style={btnGhost}>Testar</button>
            <button onClick={handleDisable} disabled={busy} style={btnDanger}>Desativar</button>
          </>
        )}
      </div>
      {(error || testStatus) && (
        <div style={{ fontSize: 12, marginTop: 8, color: error ? '#991B1B' : '#15803D' }}>
          {error || testStatus}
        </div>
      )}
    </div>
  );
}

const cardStyleSubtle = { marginBottom: 20, padding: 14, background: '#F1F5F9', borderTop: '3px solid #94A3B8' };
const cardStyleWarn   = { marginBottom: 20, padding: 14, background: '#FEF3C7', borderTop: '3px solid #F59E0B' };
const cardStyleOn     = { marginBottom: 20, padding: 14, background: '#F0FDF4', borderTop: '3px solid #16A34A' };
const cardStyleOff    = { marginBottom: 20, padding: 14, background: '#FAFAF7', borderTop: '3px solid #F5C518' };

const btnPrimary = { padding: '8px 18px', borderRadius: 6, border: 'none', background: '#1E3A4A', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' };
const btnGhost   = { padding: '6px 14px', borderRadius: 6, border: '1px solid #CBD5E1', background: '#fff', color: '#475569', fontWeight: 600, fontSize: 12, cursor: 'pointer' };
const btnDanger  = { padding: '6px 14px', borderRadius: 6, border: '1px solid #FCA5A5', background: '#FEF2F2', color: '#991B1B', fontWeight: 600, fontSize: 12, cursor: 'pointer' };
